import { User } from '../user/User.model';
import { EventUserDTO } from '../../dto/event/EventUserDTO';
import { EducationalPath } from '../educational-path/EducationalPath.model';
import { Location } from '../location/Location.model';
import { UserLocationDTO } from '../../dto/users/user-location.dto';
import { UserStatusDTO } from '../../dto/users/user-status.dto';
import { LocationDTO } from '../../dto/location/location.dto';

export class EventUser extends User {
  event_educational_paths: EducationalPath[] = [];
  location: Location | null = null;
  location_name: string | null = null;

  constructor(
    uuid: string,
    first_name: string,
    last_name: string,
    user: string | null = null,
    roles: any = null,
    organizations_id: number | null = null,
    event_educational_paths: EducationalPath[] = [],
    location: Location | null = null,
    location_name: string | null = null,
    locations: UserLocationDTO[] | null = null,
    status: UserStatusDTO | null = null
  ) {
    super(uuid, first_name, last_name, user, roles, organizations_id, locations, status);
    this.event_educational_paths = event_educational_paths;
    this.location = location;
    this.location_name = location_name;
  }

  static override fromDTO(dto: EventUserDTO): EventUser {
    const eventEducationalPaths = dto.educational_paths?.map(epDto => 
      EducationalPath.fromDTO(epDto)) || [];
    
    const location = dto.location ? 
      Location.fromDTO(dto.location) : null;

    // Convert LocationDTO[] to UserLocationDTO[] if needed
    const userLocations = dto.locations?.map(loc => ({
      id: 0,
      guid: loc.guid,
      locations_name: loc.locations_name || '',
      role_name: 'default'
    } as UserLocationDTO)) || null;

    return new EventUser(
      dto.uuid,
      dto.first_name,
      dto.last_name,
      dto.user,
      dto.roles,
      dto.organizations_id,
      eventEducationalPaths,
      location,
      dto.location_name,
      userLocations,
      dto.status
    );
  }
  
  override get name(): string {
    return `${this.first_name} ${this.last_name}`;
  }
  
  override get primaryLocation(): string {
    return this.location_name || '-';
  }
  
  get educationalPathsText(): string {
    if (!this.event_educational_paths || this.event_educational_paths.length === 0) {
      return '-';
    }
    
    return this.event_educational_paths.map(ep => {
      // Use the same logic as other components: prefer name, fall back to full_name, show compact version
      const displayName = ep.name && ep.name.trim() && !this.isPlaceholderLabel(ep.name) 
        ? ep.name 
        : (ep.full_name && ep.full_name.trim() ? ep.full_name : 'Unknown Path');
      
      // Return compact 4-character version
      return displayName.length > 4 ? displayName.substring(0, 4).toUpperCase() : displayName.toUpperCase();
    }).join(', ');
  }

  private isPlaceholderLabel(value: string | null): boolean {
    if (!value) return true;
    const v = value.trim().toLowerCase();
    if (v.length === 0) return true;

    // Common placeholder variants coming from backend or defaults
    const placeholders = new Set([
      'unknown path',
      'unknown',
      'unavngivet uddannelsessti',
      'unavngivet',
      'ukendt uddannelsessti',
      'ukendt',
      'unavngivet sti',
      'null',  // Handle string "null" from backend
      'undefined'  // Handle string "undefined" from backend
    ]);
    return placeholders.has(v);
  }
} 